import React, { useState, useEffect } from 'react';
import { denialPreventionApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  ShieldAlert, ShieldCheck, AlertTriangle, TrendingDown,
  DollarSign, BarChart3, Activity, Loader2, Database, ArrowDown, ArrowUp
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const RISK_COLORS = { low: '#166534', medium: '#92400E', high: '#DC2626', critical: '#7F1D1D' };
const PIE_COLORS = ['#166534', '#92400E', '#DC2626', '#7F1D1D'];

export default function DenialPreventionPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await denialPreventionApi.dashboard();
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await denialPreventionApi.seedDemo();
      toast.success('Demo data seeded');
      load();
    } catch (err) {
      toast.error('Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" />
    </div>
  );

  const s = data?.summary || {};
  const riskDist = data?.risk_distribution || {};
  const pieData = Object.entries(riskDist).filter(([,v]) => v > 0).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v }));

  return (
    <div className="space-y-6" data-testid="denial-prevention-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Denial Prevention Dashboard
          </h1>
          <p className="text-[#64748B] mt-1">AI-powered claim denial analytics and prevention metrics</p>
        </div>
        {s.total_analyzed === 0 && (
          <Button onClick={seedDemo} disabled={seeding} className="careiq-btn-primary" data-testid="seed-denial-data-btn">
            <Database className="w-4 h-4 mr-2" />
            {seeding ? 'Seeding...' : 'Seed Demo Data'}
          </Button>
        )}
      </div>

      {s.total_analyzed === 0 ? (
        <Card className="careiq-card">
          <CardContent className="py-16 text-center">
            <ShieldAlert className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
            <p className="text-lg text-[#64748B]">No denial predictions yet</p>
            <p className="text-sm text-[#94A3B8] mt-1">Run AI Check on claims to start building prevention data, or seed demo data.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI Strip */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="denial-kpis">
            {[
              { label: 'Claims Analyzed', value: s.total_analyzed, icon: BarChart3, color: '#0E7490' },
              { label: 'Avg Risk Score', value: s.avg_risk_score, icon: Activity, color: s.avg_risk_score > 50 ? '#DC2626' : '#92400E' },
              { label: 'High Risk Flagged', value: s.high_risk_count, icon: ShieldAlert, color: '#DC2626' },
              { label: 'Est. Denials Prevented', value: s.prevented_denials, icon: ShieldCheck, color: '#166534' },
              { label: 'Est. Savings', value: `$${(s.savings_estimate || 0).toLocaleString()}`, icon: DollarSign, color: '#166534' },
            ].map((kpi, i) => (
              <Card key={i} className="careiq-metric-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: kpi.color + '15' }}>
                      <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#0A2342]">{kpi.value}</div>
                      <div className="text-xs text-[#64748B]">{kpi.label}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Risk Distribution Pie */}
            <Card className="careiq-card" data-testid="risk-distribution-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#0A2342]">Risk Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {Object.entries(RISK_COLORS).map(([k, c]) => (
                    <span key={k} className="flex items-center gap-1 text-xs text-[#64748B]">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
                      {k.charAt(0).toUpperCase() + k.slice(1)} ({riskDist[k] || 0})
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Trend Chart */}
            <Card className="careiq-card" data-testid="trend-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#0A2342]">Monthly Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={data?.trend_data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="analyzed" stroke="#0E7490" strokeWidth={2} name="Analyzed" />
                    <Line type="monotone" dataKey="avg_risk" stroke="#DC2626" strokeWidth={2} name="Avg Risk" />
                    <Line type="monotone" dataKey="high_risk" stroke="#7F1D1D" strokeWidth={2} name="High Risk" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Top Triggers */}
          <Card className="careiq-card" data-testid="top-triggers">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#92400E]" />
                Top Denial Triggers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data?.top_triggers || []).length > 0 ? (
                <div className="space-y-3">
                  {data.top_triggers.map((t, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" data-testid={`trigger-${i}`}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: RISK_COLORS[t.severity] + '15', color: RISK_COLORS[t.severity] }}>
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm text-[#0A2342]">{t.trigger}</div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium`} style={{ backgroundColor: RISK_COLORS[t.severity] + '15', color: RISK_COLORS[t.severity] }}>
                          {t.severity}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-[#0A2342]">{t.count}</div>
                        <div className="text-[10px] text-[#64748B]">occurrences</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#64748B] py-8">No triggers recorded yet</p>
              )}
            </CardContent>
          </Card>

          {/* Denial Rate Context */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="careiq-card" data-testid="denial-rate-context">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#0A2342]">Practice Denial Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                  <div>
                    <div className="text-sm text-[#64748B]">Current Denial Rate</div>
                    <div className="text-3xl font-bold" style={{ color: (s.denial_rate || 0) > 10 ? '#DC2626' : '#166534' }}>{s.denial_rate || 0}%</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-[#64748B]">Industry Benchmark</div>
                    <div className="text-3xl font-bold text-[#0E7490]">&lt;10%</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-[#991B1B]/5">
                    <div className="text-xs text-[#64748B]">Total Denied</div>
                    <div className="text-xl font-bold text-[#991B1B]">{s.total_denied || 0}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-[#0E7490]/5">
                    <div className="text-xs text-[#64748B]">Total Claims</div>
                    <div className="text-xl font-bold text-[#0E7490]">{s.total_claims || 0}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Predictions */}
            <Card className="careiq-card" data-testid="recent-predictions">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#0A2342]">Recent Predictions</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[300px]">
                  <div className="space-y-2">
                    {(data?.recent_predictions || []).slice(0, 10).map((p, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F8FAFC] transition-colors">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: RISK_COLORS[p.risk_level] + '15' }}>
                          <span className="text-sm font-bold" style={{ color: RISK_COLORS[p.risk_level] }}>{p.risk_score}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-mono text-[#0E7490] truncate">{p.claim_id || 'N/A'}</div>
                          <div className="text-xs text-[#64748B]">{p.insurance_provider} &middot; ${p.total_amount?.toLocaleString()}</div>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: RISK_COLORS[p.risk_level] + '15', color: RISK_COLORS[p.risk_level] }}>
                          {p.risk_level}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
